import { useContext } from 'react';

import ScorecardsContext from 'contexts/ScorecardsContext';

type KpiListContent = Pick<Studio.ChartContent, 'visType'> & {
  table?: Studio.TableOptions;
};

// Periods a KPI list can be set to show
export const MIN_KPI_LIST_PERIODS = 1;
export const MAX_KPI_LIST_PERIODS = 52;

export const isKpiListTable = (content: KpiListContent): boolean =>
  content.visType === 'table' && content.table?.rows === 'kpiList';

export const getKpiListScorecardId = (
  content: KpiListContent,
): string | undefined =>
  isKpiListTable(content) ? content.table?.kpiList?.scorecardId : undefined;

// The periods setting as it can be saved: a whole number in range, or
// absent for the default
export const toKpiListPeriods = (value: unknown): number | undefined => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return Math.min(
    MAX_KPI_LIST_PERIODS,
    Math.max(MIN_KPI_LIST_PERIODS, Math.round(value)),
  );
};

// The classic Scorecard card it saves as. Firestore rejects undefined, so
// the default periods leave the field out.
export const toScorecardCardContent = ({
  scorecardId,
  numberOfPeriods,
}: {
  scorecardId: string;
  numberOfPeriods?: number;
}): CanvasCard.Content.Scorecard => {
  const periods = toKpiListPeriods(numberOfPeriods);
  return periods === undefined
    ? { type: 'Scorecard', scorecardId }
    : { type: 'Scorecard', scorecardId, numberOfPeriods: periods };
};

// The KPI lists a Composite's elements draw
export const getCompositeScorecardIds = (
  rows: Studio.CompositeRow[] | undefined,
): string[] =>
  (rows ?? []).flatMap((row) =>
    row.elements.flatMap((element) => {
      const scorecardId = getKpiListScorecardId(element);
      return scorecardId ? [scorecardId] : [];
    }),
  );

// The default periods: 3 on a dashboard, otherwise the KPI List's own
export const getDefaultKpiListPeriods = ({
  contentType,
  scorecard,
}: {
  contentType: Studio.ContentType;
  scorecard?: Scorecards.Scorecard;
}): number =>
  contentType === 'dashboard'
    ? 3
    : (scorecard && scorecard.defaultSelectedNumberOfPeriods) || 12;

// The KPI List a block draws. Read from the list rather than the lookup,
// which follows it an effect later, so one just created is found as soon
// as it's loaded.
export const useKpiListScorecard = (
  content: KpiListContent,
): { scorecard?: Scorecards.Scorecard; isLoading: boolean } => {
  const { scorecards, isLoading } = useContext(ScorecardsContext);
  const scorecardId = getKpiListScorecardId(content);
  return {
    scorecard: scorecardId
      ? scorecards.find((s) => s.id === scorecardId)
      : undefined,
    isLoading,
  };
};
